if (!customElements.get('easy-donation-bridge')) {
  customElements.define(
    'easy-donation-bridge',
    class EasyDonationBridge extends HTMLElement {
      connectedCallback() {
        this.status = this.querySelector('[data-easy-donation-status]');
        this.shop = this.dataset.shop || window.Shopify?.shop;
        this.cartUrl = this.dataset.cartUrl || '/cart';
        this.productId = this.dataset.productId;

        this.loadDonationSettings();
      }

      async loadDonationSettings() {
        if (!this.shop) {
          this.renderError('Donation options are unavailable.');
          return;
        }

        try {
          const response = await fetch('/apps/easyDonationApp/public/newLayout', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: new URLSearchParams({ shop: this.shop }),
          });

          if (!response.ok) throw new Error('Donation settings request failed');

          const data = await response.json();
          const donations = Array.isArray(data.donationSettings)
            ? data.donationSettings.filter((donation) => donation.status === '1')
            : [];

          if (!donations.length) {
            this.renderError('Donation options are unavailable.');
            return;
          }

          const productDonation = donations.find((donation) => String(donation.product_id) === String(this.productId));

          if (!productDonation) {
            this.renderError('Donation options are unavailable.');
            return;
          }

          this.renderDonationForm(productDonation);
        } catch (error) {
          console.error('Donation load failed:', error);
          this.renderError('Donation options are unavailable.');
        }
      }

      renderDonationForm(donation) {
        const presetAmounts = this.parseList(donation.donation_preset_values);
        const presetVariantIds = this.parseList(donation.custom_variant_ids);
        const hasOtherAmount = donation.is_other_option_enabled === '1';
        const defaultSelection = presetAmounts[0] && presetVariantIds[0] ? 'preset-0' : hasOtherAmount ? 'other' : '';

        this.innerHTML = `
          <form class="foundation-easy-donation__form" data-easy-donation-form>
            <fieldset class="foundation-easy-donation__fieldset">
              <legend class="foundation-easy-donation__legend visually-hidden">Donation amount</legend>
              ${
                presetAmounts.length
                  ? `<div class="foundation-easy-donation__amounts" role="group" aria-label="Donation amount">
                      ${presetAmounts
                        .map((amount, index) => {
                          const variantId = presetVariantIds[index] || '';
                          if (!variantId) return '';
                          return `
                            <button
                              type="button"
                              class="foundation-easy-donation__amount-button"
                              data-easy-donation-preset
                              data-selection="preset-${index}"
                              data-variant-id="${this.escapeHtml(variantId)}"
                              data-amount="${this.escapeHtml(amount)}"
                              aria-pressed="${defaultSelection === `preset-${index}` ? 'true' : 'false'}"
                            >
                              $${this.escapeHtml(amount)}
                            </button>
                          `;
                        })
                        .join('')}
                    </div>`
                  : ''
              }
              ${
                hasOtherAmount
                  ? `<label class="foundation-easy-donation__other">
                      <span class="foundation-easy-donation__other-label">${this.escapeHtml(
                        donation.other_option_text || 'Other'
                      )}</span>
                      <span class="foundation-easy-donation__other-field">
                        <span class="foundation-easy-donation__currency" aria-hidden="true">$</span>
                        <input
                          class="foundation-easy-donation__other-input"
                          data-easy-donation-other-input
                          type="text"
                          inputmode="numeric"
                          pattern="[1-9][0-9]*"
                          autocomplete="off"
                          aria-label="${this.escapeHtml(donation.other_option_text || 'Other amount')}"
                        >
                        <span class="foundation-easy-donation__cents" aria-hidden="true">.00</span>
                      </span>
                    </label>`
                  : ''
              }
              <div class="foundation-easy-donation__error" data-easy-donation-error hidden></div>
              <button type="submit" class="foundation-action-button foundation-action-button--primary foundation-easy-donation__submit" data-easy-donation-submit>
                Make Donation
              </button>
            </fieldset>
          </form>
        `;

        this.form = this.querySelector('[data-easy-donation-form]');
        this.error = this.querySelector('[data-easy-donation-error]');
        this.submitButton = this.querySelector('[data-easy-donation-submit]');
        this.otherInput = this.querySelector('[data-easy-donation-other-input]');
        this.presetButtons = this.querySelectorAll('[data-easy-donation-preset]');
        this.selected = defaultSelection;
        this.donationProductId = donation.product_id;
        this.donationTitle = donation.donation_name || 'Donation';

        this.presetButtons.forEach((button) => {
          button.classList.toggle('is-selected', button.dataset.selection === this.selected);
          button.addEventListener('click', () => this.selectPreset(button));
        });
        this.otherInput?.addEventListener('input', () => this.selectOtherAmount());
        this.form.addEventListener('submit', (event) => this.submitDonation(event));
      }

      selectPreset(button) {
        this.selected = button.dataset.selection;
        if (this.otherInput) this.otherInput.value = '';
        this.presetButtons.forEach((presetButton) => {
          const isSelected = presetButton === button;
          presetButton.classList.toggle('is-selected', isSelected);
          presetButton.setAttribute('aria-pressed', isSelected);
        });
        this.clearError();
      }

      selectOtherAmount() {
        this.selected = 'other';
        this.otherInput.value = this.otherInput.value.replace(/\D/g, '').replace(/^0+/, '');
        this.presetButtons.forEach((button) => {
          button.classList.remove('is-selected');
          button.setAttribute('aria-pressed', 'false');
        });
        this.clearError();
      }

      async submitDonation(event) {
        event.preventDefault();
        this.clearError();
        this.setLoading(true);

        try {
          if (this.selected === 'other') {
            await this.submitCustomDonation();
          } else {
            const selectedButton = [...this.presetButtons].find((button) => button.dataset.selection === this.selected);
            if (!selectedButton?.dataset.variantId) throw new Error('Select a donation amount.');
            const amount = selectedButton.dataset.amount;
            await this.addItemsToCart([
              {
                id: Number(selectedButton.dataset.variantId),
                quantity: 1,
                properties: this.createDonationLineProperties(amount, this.createDonationGroupId()),
              },
            ]);
          }

          window.location.href = this.cartUrl;
        } catch (error) {
          this.renderFormError(error.message || 'Donation could not be added.');
          this.setLoading(false);
        }
      }

      async submitCustomDonation() {
        const amount = this.otherInput?.value;
        if (!amount || Number(amount) <= 0) throw new Error('Enter a valid donation amount.');

        const response = await fetch('/apps/easyDonationApp/public/customDonation', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
          body: new URLSearchParams({
            shop: this.shop,
            amount,
            product_id: this.donationProductId,
          }),
        });

        if (!response.ok) throw new Error('Donation could not be created.');

        const data = await response.json();
        if (!data?.response) throw new Error('Donation could not be created.');

        const groupId = this.createDonationGroupId();
        const items = Object.entries(data.response).map(([id, quantity]) => ({
          id: Number(id),
          quantity: Number(quantity),
          properties: this.createDonationLineProperties(amount, groupId),
        }));

        await this.addItemsToCart(items);
      }

      createDonationLineProperties(amount, groupId) {
        return {
          _foundation_easy_donation_group: groupId,
          _foundation_easy_donation_amount: amount,
          _foundation_easy_donation_title: this.donationTitle,
          _foundation_easy_donation_product_id: this.donationProductId,
        };
      }

      createDonationGroupId() {
        if (window.crypto?.randomUUID) return `foundation-ed-${window.crypto.randomUUID()}`;
        return `foundation-ed-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      async addItemsToCart(items) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error('Donation could not be added to cart.');
      }

      renderError(message) {
        this.innerHTML = `<div class="foundation-easy-donation__status">${this.escapeHtml(message)}</div>`;
      }

      renderFormError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = false;
      }

      clearError() {
        if (!this.error) return;
        this.error.textContent = '';
        this.error.hidden = true;
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('is-loading', isLoading);
      }

      parseList(value) {
        return String(value || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (character) => {
          const entities = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
          };
          return entities[character];
        });
      }
    }
  );
}
